import SalesActivityActionCreators from "../../store/actions/SalesActivity";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../store";
import {
	ZomentumEntities,
	ZomentumEntityRequest,
	ZomentumRoutes
} from "@/Contracts";
import { RecentActivityFeedType } from "@/Contracts/Others/RecentActivityFeed";
import {
	SalesActivityRequest,
	SalesActivitySection
} from "@/Contracts/Others/SalesActivity";
import { useContext } from "react";
import { ParentContext } from "../../store/context/ParentContext";
import { isVendorApp } from "@/Utils";

const useRefreshSalesActivity = (): (() => void) => {
	const parentContext = useContext(ParentContext);
	const { currentActiveOpportunity } = parentContext.opportunity;
	const { currentActiveClient } = parentContext.client;
	const { currentActiveVendorClient } = parentContext.vendorClient;
	const { currentActivePartner } = parentContext.vendorPartner;
	const {
		activeFilters,
		salesActivityActiveTab,
		allSalesActivity,
		tasksSalesActivity,
		notesSalesActivity,
		meetingsSalesActivity,
		callsSalesActivity,
		emailsSalesActivity
	} = useSalesActivityModuleSelector((state: SalesActivityModuleState) => {
		return {
			activeFilters: state.salesActivity.activeFilters,
			salesActivityActiveTab: state.salesActivity.salesActivityActiveTab,
			allSalesActivity: state.salesActivity.allSalesActivity,
			tasksSalesActivity: state.salesActivity.tasksSalesActivity,
			notesSalesActivity: state.salesActivity.notesSalesActivity,
			meetingsSalesActivity: state.salesActivity.meetingsSalesActivity,
			callsSalesActivity: state.salesActivity.callsSalesActivity,
			emailsSalesActivity: state.salesActivity.emailsSalesActivity
		};
	});

	const dispatch = useSalesActivityModuleDispatch();

	const getActiveEntityId = () => {
		if (isVendorApp()) {
			const isPartnersPage = window.location.pathname.startsWith(
				`${ZomentumRoutes.VendorPartnerList}/`
			);
			const isClientsPage = window.location.pathname.startsWith(
				`${ZomentumRoutes.VendorClientList}/`
			);
			const activeEntityId = isPartnersPage
				? currentActivePartner?.id ?? ""
				: isClientsPage
				? currentActiveVendorClient?.id ?? ""
				: currentActiveOpportunity?.id ?? "";
			return activeEntityId;
		} else {
			const activeEntityId = currentActiveOpportunity
				? currentActiveOpportunity.id
				: currentActiveClient
				? currentActiveClient?.id ?? ""
				: "";
			return activeEntityId;
		}
	};

	const getActiveEntityType = () => {
		if (isVendorApp()) {
			const isPartnersPage = window.location.pathname.startsWith(
				`${ZomentumRoutes.VendorPartnerList}/`
			);
			const isClientsPage = window.location.pathname.startsWith(
				`${ZomentumRoutes.VendorClientList}/`
			);
			const activeEntityType = isPartnersPage
				? ZomentumEntities.VendorCompany
				: isClientsPage
				? ZomentumEntities.VendorClient
				: ZomentumEntities.VendorOpportunity;
			return activeEntityType;
		} else {
			const activeEntityType = !!currentActiveOpportunity
				? ZomentumEntities.Opportunity
				: !!currentActiveClient
				? ZomentumEntities.Client
				: ZomentumEntities.Opportunity;
			return activeEntityType;
		}
	};

	const refreshAllTabSalesActivity = async (
		salesActivityRequest: SalesActivityRequest
	) => {
		try {
			dispatch(SalesActivityActionCreators.showSalesActivityLoader());
			salesActivityRequest.next =
				allSalesActivity &&
				allSalesActivity[SalesActivitySection.Overdue]
					? allSalesActivity[SalesActivitySection.Overdue].next
					: null;
			await dispatch(
				SalesActivityActionCreators.getAllOverdueSalesActivity(
					salesActivityRequest
				)
			);
			salesActivityRequest.next =
				allSalesActivity &&
				allSalesActivity[SalesActivitySection.Upcoming]
					? allSalesActivity[SalesActivitySection.Upcoming].next
					: null;
			await dispatch(
				SalesActivityActionCreators.getAllUpcomingSalesActivity(
					salesActivityRequest
				)
			);
			salesActivityRequest.next =
				allSalesActivity &&
				allSalesActivity[SalesActivitySection.Completed]
					? allSalesActivity[SalesActivitySection.Completed].next
					: null;
			await dispatch(
				SalesActivityActionCreators.getAllCompletedSalesActivity(
					salesActivityRequest
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const refreshEntitySalesActivity = async (
		entity: RecentActivityFeedType,
		salesActivityRequest: SalesActivityRequest
	) => {
		try {
			dispatch(SalesActivityActionCreators.showSalesActivityLoader());

			switch (entity) {
				case RecentActivityFeedType.Task: {
					salesActivityRequest.next =
						tasksSalesActivity &&
						tasksSalesActivity[SalesActivitySection.Overdue]
							? tasksSalesActivity[SalesActivitySection.Overdue]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getTasksOverdueSalesActivity(
							salesActivityRequest
						)
					);
					salesActivityRequest.next =
						tasksSalesActivity &&
						tasksSalesActivity[SalesActivitySection.Upcoming]
							? tasksSalesActivity[SalesActivitySection.Upcoming]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getTasksUpcomingSalesActivity(
							salesActivityRequest
						)
					);
					salesActivityRequest.next =
						tasksSalesActivity &&
						tasksSalesActivity[SalesActivitySection.Completed]
							? tasksSalesActivity[SalesActivitySection.Completed]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getTasksCompletedSalesActivity(
							salesActivityRequest
						)
					);
					return;
				}
				case RecentActivityFeedType.Comment: {
					salesActivityRequest.next =
						notesSalesActivity &&
						notesSalesActivity[SalesActivitySection.Pinned]
							? notesSalesActivity[SalesActivitySection.Pinned]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getNotesPinnedSalesActivity(
							salesActivityRequest
						)
					);
					salesActivityRequest.next =
						notesSalesActivity &&
						notesSalesActivity[SalesActivitySection.Completed]
							? notesSalesActivity[SalesActivitySection.Completed]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getNotesCompletedSalesActivity(
							salesActivityRequest
						)
					);
					return;
				}
				case RecentActivityFeedType.Meeting: {
					salesActivityRequest.next =
						meetingsSalesActivity &&
						meetingsSalesActivity[SalesActivitySection.Upcoming]
							? meetingsSalesActivity[
									SalesActivitySection.Upcoming
							  ].next
							: null;
					await dispatch(
						SalesActivityActionCreators.getMeetingsUpcomingSalesActivity(
							salesActivityRequest
						)
					);
					salesActivityRequest.next =
						meetingsSalesActivity &&
						meetingsSalesActivity[SalesActivitySection.Completed]
							? meetingsSalesActivity[
									SalesActivitySection.Completed
							  ].next
							: null;
					await dispatch(
						SalesActivityActionCreators.getMeetingsCompletedSalesActivity(
							salesActivityRequest
						)
					);
					return;
				}
				case RecentActivityFeedType.LogCall: {
					salesActivityRequest.next =
						callsSalesActivity &&
						callsSalesActivity[SalesActivitySection.Completed]
							? callsSalesActivity[SalesActivitySection.Completed]
									.next
							: null;
					await dispatch(
						SalesActivityActionCreators.getCallsSalesActivity(
							salesActivityRequest
						)
					);
					return;
				}
				case RecentActivityFeedType.LogEmail: {
					salesActivityRequest.next =
						emailsSalesActivity &&
						emailsSalesActivity[SalesActivitySection.Completed]
							? emailsSalesActivity[
									SalesActivitySection.Completed
							  ].next
							: null;
					await dispatch(
						SalesActivityActionCreators.getEmailsSalesActivity(
							salesActivityRequest
						)
					);
					return;
				}
				case RecentActivityFeedType.History:
					return;
				default:
					console.error("Invalid refresh option");
					return;
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const refreshSalesActivity = async () => {
		const activeEntityId = getActiveEntityId();

		const activeEntityType = getActiveEntityType();

		const isClientEntity = activeEntityType === ZomentumEntities.Client;

		const relatedOpportunities = isClientEntity
			? currentActiveClient?.opportunities?.data?.map(
					opportunity => opportunity?.id
			  ) ?? []
			: [];

		const salesActivityRequest = new SalesActivityRequest(
			new ZomentumEntityRequest(activeEntityType, activeEntityId),
			null,
			false,
			activeFilters,
			relatedOpportunities
		);

		if (salesActivityActiveTab === RecentActivityFeedType.All) {
			await refreshAllTabSalesActivity(salesActivityRequest);
		} else if (salesActivityActiveTab) {
			refreshEntitySalesActivity(
				salesActivityActiveTab,
				salesActivityRequest
			);
		}
	};

	return refreshSalesActivity;
};

export default useRefreshSalesActivity;
